import React from 'react';
import Icon from '../../../components/AppIcon';

const formatPrice = (price) => {
  if (!price) return 'N/A';
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 })?.format(price);
};

const ComparisonSummary = ({ properties }) => {
  const prices = properties?.map(p => p?.price_or_rent)?.filter(Boolean);
  const lowestPrice = prices?.length ? Math.min(...prices) : null;
  const highestPrice = prices?.length ? Math.max(...prices) : null;
  const priceDiff = lowestPrice && highestPrice ? highestPrice - lowestPrice : null;

  const verdictOrder = ['fair', 'underpriced', 'risky', 'aggressive', 'overpriced'];
  const ranked = [...properties]?.sort((a, b) => {
    const aV = verdictOrder?.indexOf(a?.aiAnalysis?.verdict?.toLowerCase() || '');
    const bV = verdictOrder?.indexOf(b?.aiAnalysis?.verdict?.toLowerCase() || '');
    const aScore = aV === -1 ? 99 : aV;
    const bScore = bV === -1 ? 99 : bV;
    if (aScore !== bScore) return aScore - bScore;
    return (a?.price_or_rent || Infinity) - (b?.price_or_rent || Infinity);
  });

  const topPick = ranked?.[0];

  return (
    <div
      className="mt-8 rounded-2xl p-6"
      style={{ background: '#0D1B2A', border: '1px solid rgba(0, 212, 255, 0.15)' }}
    >
      <div className="flex items-center gap-2 mb-6">
        <Icon name="BarChart2" size={20} color="#00D4FF" />
        <h2 className="text-lg font-bold text-white">Comparison Summary</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Price range */}
        <div
          className="rounded-xl p-4"
          style={{ background: 'rgba(0, 212, 255, 0.05)', border: '1px solid rgba(0, 212, 255, 0.12)' }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Icon name="DollarSign" size={16} color="#00D4FF" />
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#6B9EC4' }}>Price Range</span>
          </div>
          <p className="text-sm font-bold text-white">{formatPrice(lowestPrice)}</p>
          <p className="text-xs mt-0.5" style={{ color: '#6B9EC4' }}>to {formatPrice(highestPrice)}</p>
          {priceDiff && (
            <p className="text-xs mt-1 font-medium" style={{ color: '#F59E0B' }}>
              {formatPrice(priceDiff)} difference
            </p>
          )}
        </div>

        {/* Properties count */}
        <div
          className="rounded-xl p-4"
          style={{ background: 'rgba(0, 212, 255, 0.05)', border: '1px solid rgba(0, 212, 255, 0.12)' }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Home" size={16} color="#00D4FF" />
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#6B9EC4' }}>Properties</span>
          </div>
          <p className="text-2xl font-bold text-white">{properties?.length}</p>
          <p className="text-xs mt-0.5" style={{ color: '#6B9EC4' }}>being compared</p>
        </div>

        {/* Top pick */}
        <div
          className="rounded-xl p-4"
          style={{ background: 'rgba(34, 197, 94, 0.05)', border: '1px solid rgba(34, 197, 94, 0.2)' }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Award" size={16} color="#22C55E" />
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#22C55E' }}>Top Recommendation</span>
          </div>
          <p className="text-sm font-bold text-white leading-tight">
            {topPick?.properties?.address || 'N/A'}
          </p>
          <p className="text-xs mt-0.5" style={{ color: '#6B9EC4' }}>
            {topPick?.aiAnalysis?.verdict
              ? `Verdict: ${topPick?.aiAnalysis?.verdict}`
              : formatPrice(topPick?.price_or_rent)}
          </p>
        </div>
      </div>
      {/* Ranking list */}
      <div>
        <h3 className="text-sm font-semibold mb-3" style={{ color: '#8BA5C0' }}>Recommendation Ranking</h3>
        <div className="space-y-2">
          {ranked?.map((prop, idx) => (
            <div
              key={prop?.id || idx}
              className="flex items-center gap-3 px-4 py-3 rounded-xl"
              style={{
                background: idx === 0 ? 'rgba(34, 197, 94, 0.08)' : 'rgba(255,255,255,0.03)',
                border: idx === 0 ? '1px solid rgba(34, 197, 94, 0.2)' : '1px solid rgba(255,255,255,0.06)'
              }}
            >
              <span
                className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                style={{
                  background: idx === 0 ? 'rgba(34, 197, 94, 0.2)' : 'rgba(0, 212, 255, 0.1)',
                  color: idx === 0 ? '#22C55E' : '#6B9EC4'
                }}
              >
                {idx + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{prop?.properties?.address}</p>
                <p className="text-xs" style={{ color: '#6B9EC4' }}>
                  {prop?.properties?.suburb}{prop?.properties?.state ? ', ' + prop?.properties?.state : ''}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-bold" style={{ color: idx === 0 ? '#22C55E' : '#E2E8F0' }}>
                  {new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 })?.format(prop?.price_or_rent || 0)}
                </p>
                {prop?.aiAnalysis?.verdict && (
                  <p className="text-xs capitalize" style={{ color: '#8BA5C0' }}>{prop?.aiAnalysis?.verdict}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComparisonSummary;
